// @ts-strict-ignore
import type { AccessTokenProvider } from '@empathyco/playboard-core-domain'
import type { Location, Route } from 'vue-router'
import type { Store } from 'vuex'

import type { AppConfig, GuardResolveFunction } from '../models'
import type { AuthTokenService } from '../services'

import { CONTAINER } from '../config'
import { TYPES } from '../config/inversify.types'

import { GuardClass } from '../models'

import { ROUTER_CONSTS } from '../router/router.consts'

import { AUTH_CONSTS } from '../store/auth/auth.consts'

import { OIDC_CONSTS } from '../store/oidc/oidc.consts'

export class LoginGuard extends GuardClass {
  static async execute(
    this: void,
    to: Route,
    from: Route,
    next: GuardResolveFunction,
  ): Promise<void> {
    let redirectLocation: Location
    const store = CONTAINER.get<Store<any>>(TYPES.Store)
    const { oidcLogin, oidcLogout } = CONTAINER.get<AppConfig>(TYPES.AppConfig)

    if (oidcLogin) {
      const oidcIsAuthenticated: boolean =
        store.getters[`${OIDC_CONSTS.moduleName}/${OIDC_CONSTS.getters.oidcIsAuthenticated}`]

      if (to.name === ROUTER_CONSTS.routes.login) {
        if (oidcIsAuthenticated || oidcLogout) {
          redirectLocation = {
            ...to,
            name: ROUTER_CONSTS.routes.home,
          }
        }
      } else if (!oidcIsAuthenticated) {
        // I think always arrives here, the "code" is in the url
        // but to avoid some corner case, better to have this "if" before
        if (to.query.code) {
          const login: string = await store.dispatch(
            `${OIDC_CONSTS.moduleName}/${OIDC_CONSTS.actions.oidcSignInCallback}`,
          )

          redirectLocation = {
            path: login,
          }
        }
      } else {
        const redirectPath = to.query[ROUTER_CONSTS.query.redirect] as string
        if (redirectPath) {
          redirectLocation = {
            path: redirectPath,
          }
        }
      }
    } else {
      let token = await CONTAINER.get<AccessTokenProvider>(TYPES.AccessTokenProvider)()
      if (!token) {
        token = await store.dispatch(
          `${AUTH_CONSTS.moduleName}/${AUTH_CONSTS.actions.loadStateFromStorage}`,
        )
      }
      const keepLoggedIn: boolean =
        store.getters[`${AUTH_CONSTS.moduleName}/${AUTH_CONSTS.getters.keepLoggedIn}`]
      const authTokenService: AuthTokenService = CONTAINER.get<AuthTokenService>(
        TYPES.AuthTokenService,
      )
      if (!!token && keepLoggedIn && authTokenService.isNearToExpire(token, 1000 * 60 * 10)) {
        try {
          token = await store.dispatch(`${AUTH_CONSTS.moduleName}/${AUTH_CONSTS.actions.refresh}`)
        } catch (error) {}
      }
      const tokenIsExpired = token ? authTokenService.isExpired(token) : true
      if (tokenIsExpired) {
        if (to.name !== ROUTER_CONSTS.routes.login) {
          redirectLocation = {
            name: ROUTER_CONSTS.routes.logout,
          }
          if (to.path !== '/') {
            redirectLocation.query = {
              [ROUTER_CONSTS.query.redirect]: to.fullPath,
            }
          }
        }
      } else {
        const redirectPath = to.query[ROUTER_CONSTS.query.redirect]
        if (redirectPath) {
          redirectLocation = {
            path: redirectPath as string,
          }
        } else if (to.name === ROUTER_CONSTS.routes.login) {
          redirectLocation = {
            ...to,
            name: ROUTER_CONSTS.routes.home,
          }
        }
      }
      if (redirectLocation) {
        redirectLocation.replace = true
      }
    }

    next(redirectLocation)
  }
}
